import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { RiFunctionAddLine, RiDeleteBin6Line } from "react-icons/ri";
import { BiEdit } from "react-icons/bi";
import { CgDisplayGrid } from "react-icons/cg";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useGlobalContext } from "../context/AuthContext";
import {
  getSubjectTreeBrushStyle,
  type SubjectTreeNodeStyle,
} from "./subjectTreeStyle";

interface Subject {
  id: number;
  title: string;
  parentId: number | null;
}

interface DraggableTreeNodeProps {
  node: Subject;
  childCount?: number;
  depth?: number;
  nodeStyle: SubjectTreeNodeStyle;
  onDrop: (draggedId: number, targetId: number) => void;
  onClick: (event: React.MouseEvent, node: Subject) => void;
}

const DraggableTreeNode: React.FC<DraggableTreeNodeProps> = ({
  node,
  childCount = 0,
  depth = 0,
  nodeStyle,
  onDrop,
  onClick,
}) => {
  const navigate = useNavigate();
  const { user } = useGlobalContext();
  const isSubjectModerator = user?.userType === "admin" || user?.userType === "moderator";

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: "NODE",
      item: { id: node.id },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: isSubjectModerator,
    }),
    [node.id, isSubjectModerator],
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: "NODE",
      drop: (draggedItem: { id: number }) => {
        onDrop(draggedItem.id, node.id);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
      }),
    }),
    [node.id, onDrop],
  );

  const menuItems: MenuProps["items"] = [
    {
      key: "explore",
      label: "Explore",
      icon: <CgDisplayGrid size={16} />,
    },
    { type: "divider" },
    {
      key: "add",
      label: "Add subsubject",
      icon: <RiFunctionAddLine size={16} />,
      disabled: !user,
    },
    {
      key: "edit",
      label: "Rename",
      icon: <BiEdit size={16} />,
      disabled: !isSubjectModerator,
    },
    {
      key: "gist",
      label: "Create Gist",
      icon: <CgDisplayGrid size={16} />,
      disabled: !user,
    },
    { type: "divider" },
    {
      key: "delete",
      label: "Delete Subject",
      icon: <RiDeleteBin6Line size={16} />,
      danger: true,
      disabled: !isSubjectModerator,
    },
  ];

  const handleMenuClick: MenuProps["onClick"] = (info) => {
    info.domEvent.stopPropagation();

    const syntheticEvent = {
      ...info.domEvent,
      clientX: window.innerWidth / 2,
      clientY: window.innerHeight / 3,
      stopPropagation: () => undefined,
    } as unknown as React.MouseEvent;

    switch (info.key) {
      case "explore":
        navigate(`/subject-single?subjectId=${node.id}`);
        break;
      case "add":
        onClick(syntheticEvent, { ...node, title: "__ACTION_ADD__" });
        break;
      case "edit":
        onClick(syntheticEvent, { ...node, title: "__ACTION_UPDATE__" });
        break;
      case "gist":
        onClick(syntheticEvent, { ...node, title: "__ACTION_GIST__" });
        break;
      case "delete":
        onClick(syntheticEvent, { ...node, title: "__ACTION_DELETE__" });
        break;
      default:
        break;
    }
  };

  const handleNavigate = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/subject/${node.title}/${node.id}`);
  };

  const childLabel =
    childCount === 0
      ? "No sub-subjects"
      : `${childCount} sub-subject${childCount === 1 ? "" : "s"}`;

  const normalizedDepth = Math.min(depth, 2);
  const depthClass = `subject-tree-node-depth-${normalizedDepth}`;
  const brushStyle = getSubjectTreeBrushStyle(node.id, node.title, depth, nodeStyle);
  const nodeClasses = [
    "subject-tree-node-card",
    depth === 0 ? "subject-tree-node-card-top subject-tree-node-card-focus" : "",
    normalizedDepth === 1 ? "subject-tree-node-card-mid" : "",
    normalizedDepth >= 2 ? "subject-tree-node-card-leaf" : "",
    childCount > 0 ? "subject-tree-node-card-has-children" : "subject-tree-node-card-terminal",
    depthClass,
    isDragging ? "subject-tree-node-card-dragging" : "",
    isOver ? "subject-tree-node-card-over" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={(element) => dragRef(dropRef(element))} className={nodeClasses} style={brushStyle}>
      <span className="subject-tree-node-brush" aria-hidden="true" />
      <div className="subject-tree-node-main" onClick={handleNavigate} role="button" tabIndex={0}>
        <div className="subject-tree-node-title">{node.title}</div>
        <div className="subject-tree-node-meta">{childLabel}</div>
      </div>

      {user && (
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={["click"]}
          placement="bottomRight"
          overlayClassName="subject-tree-actions-dropdown"
          arrow={{ pointAtCenter: true }}
        >
          <button
            type="button"
            className="subject-tree-node-menu"
            onClick={(event) => event.stopPropagation()}
            aria-label={`Open actions for ${node.title}`}
          >
            <BsThreeDotsVertical size={14} />
          </button>
        </Dropdown>
      )}
    </div>
  );
};

export default DraggableTreeNode;
